import {
    EtopsData,
    FlightImpactDto,
    FlightPlanRouteModel,
    FuelDto,
    FuelUnit,
    MonitorStatus,
    RouteFlownPointModel,
    RouteWaypointModel,
    WaypointType
} from '@fe/core';
import dayjs from 'dayjs';
import { Position } from 'geojson';
import AirportManager from '../../airports/airport-manager';
import Coordinate from '../../common/coordinate';
import { AlternateDestinationRecordType, AltitudeUnit, FlightStatus, ObjectClass, UpdateSource } from '../../common/enums';
import { routeFlownUnitToFuelUnit } from '../../common/helpers';
import { FeServerEtopsModel, FeServerRouteAiResponse, FeServerRouteModel } from '../../messages/message-json';
import { AlertEventCodes } from '../../monitoring/enums';
import { WorkerThresholdLevel } from '../../monitoring/models';
import { RouteAnalytics, RouteAnalyticsSegment } from '../../services/actor-messages';
import sa, { StaticAirport } from '../../static/static-airports';
import { AircraftThresholdType } from '../../types/thresholds';
import { UDViewTagSelectionEnum } from '../../udView/udview';
import Flight from '../flight';
import { getImpactEventDescription, getImpactVendorName, ImpactRecord } from '../impact-recorcd';
import { RouteFlown } from '../type';

export type { FlightImpactDto } from '@fe/core';

export interface FleetMonitorFlightDto {
    id: number;
    acid: string | null;
    acType: string | null;
    regId: string | null;
    altitude: number | null;
    speed: number;
    status: FlightStatus;
    monitorStatus: MonitorStatus;
    originMonitorStatus: MonitorStatus;
    destinationMonitorStatus: MonitorStatus;
    destinationAlternate1MonitorStatus: MonitorStatus;
    destinationAlternate2MonitorStatus: MonitorStatus;
    origin: string | null;
    destination: string | null;
    lastDestination: string | null;
    progressPercentage: number | null;
    departureTime: number | null;
    arrivalTime: number | null;
    arrivalTimeDeviation: number | null;
    departureTimeDeviation: number | null;
    edct: number | null;
    isPreDeparture: boolean;
    destinationAlternate1: string | null;
    destinationAlternate2: string | null;
    out: boolean;
    off: boolean;
    on: boolean;
    in: boolean;
    holding: boolean;
    onGround: boolean;
    fuelOnboard: FuelDto | null;
    taxiOutTime: number | null;
    taxiInTime: number | null;
}

export interface AltitudeDto {
    Value: number;
    Scale: AltitudeUnit;
}

export interface AircraftThresholdInfoItemDto {
    type: AircraftThresholdType;
    monitorStatus: MonitorStatus;
    actualValue: string | null;
    breachedLevel: WorkerThresholdLevel | null;
}

export interface HistoryItemDto {
    PositionAt: string;
    Position: Coordinate;
    Altitude: number | null;
    OnGround: boolean;
    Speed: number;
    FeedId: number | null;
    FuelOnboard: FuelDto | null;
    IsProjected: boolean;
    UpdateType: UpdateSource;
}

export interface FlightDetailsModel {
    id: number;
    acid: string | null;
    acType: string | null;
    regId: string | null;
    scratchpad: string | null;
    status: FlightStatus;
    ground: boolean;
    class: ObjectClass;
    position: Coordinate;
    lastReal: Coordinate;
    lastRealSpeed: number | null;
    lastRealAltitude: number | null;
    altitude: number | null;
    speed: number;
    origin: string | null;
    destination: string | null;
    destinationAirportPosition: Coordinate;
    originAirportPosition: Coordinate;
    distanceToDestination: number | null;
    progressPercentage: number | null;
    departureTime: number | null;
    arrivalTime: number | null;
    lastPositionReportTime: number | null;
    icaoId: number | null;
    feedId: number | null;
    userField1: string | null;
    destinationAlternate1: string | null;
    destinationAlternate2: string | null;
    out: number | null;
    off: number | null;
    on: number | null;
    in: number | null;
    fuelOnBoard: FuelDto | null;
    breachedThresholdsCount: number;
    /* Octal stored as decimal */
    beacon: number | null;
}

export interface FlightDetailsPinnedModel {
    id: number;
    acid: string | null;
    acType: string | null;
    regId: string | null; // 7 char airplane registry ID
    altitude: number | null; // always eet
    speed: number; // air speed
    origin: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    destination: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    destinationAirportPosition: Coordinate;
    originAirportPosition: Coordinate;
    progressPercentage: number | null;
    departureTime: number | null;
    arrivalTime: number | null;
    position: Coordinate;
}

export interface FlightTooltipFieldItem {
    field: UDViewTagSelectionEnum;
    value: string;
}

export interface FlightTooltipDto {
    id: number;
    acid: string | null;
    origin: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    destination: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    departureTime: number | null;
    arrivalTime: number | null;
    progressPercentage: number | null;
    hasNotes: boolean;
    customFields: FlightTooltipFieldItem[];
}

export interface FlightContextMenuDto {
    id: number;
    acid: string | null;
    status: FlightStatus;
    origin: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    destination: string | null; // 4 char ICAO airport ID (trailing space if IATA)
    alternate1: string | null;
    alternate2: string | null;
    otherAlternates: string[];
    etopsAlternates: string[];
    regId: string | null; // 7 char airplane registry ID
    oetd: string | null;
    notam: string | null;
    runway: string | null; // runway id e.g. "28L". Null or 'UNA' means unassigned.
}

export interface HoldingFlightDto {
    id: number;
    acid: string | null;
    altitude: number | null;
}

export interface FlightCurrentPositionDto {
    id: number;
    origin: string | null;
    destination: string | null;
    originAirport: StaticAirport | null;
    destinationAirport: StaticAirport | null;
    onGround: boolean;
    position: Coordinate;
    status: FlightStatus;
    arrivalTime: string | null;
    distanceToDestination: number | null;
    heading: number | null;
}

export function flight2DtoSmall(flight: Flight | null | undefined): FlightDetailsPinnedModel | null {
    if (!flight) {
        return null;
    }

    return {
        id: flight.id,
        regId: flight.registrationId,
        origin: flight.origin,
        acType: flight.aircraftType,
        acid: flight.acid,
        altitude: flight.altitude,
        arrivalTime: flight.arrivalTime,
        departureTime: flight.departureTime,
        destination: flight.destination,
        destinationAirportPosition:
            flight.destinationAirport?.lat != null && flight.destinationAirport.lng != null
                ? Coordinate.fromLatLng(flight.destinationAirport.lat, flight.destinationAirport.lng)
                : Coordinate.empty,
        originAirportPosition:
            flight.originAirport?.lat != null && flight.originAirport.lng != null
                ? Coordinate.fromLatLng(flight.originAirport.lat, flight.originAirport.lng)
                : Coordinate.empty,
        progressPercentage: flight.progressPercentage,
        speed: flight.speed,
        position: flight.position
    };
}

export function flight2TooltipDto(flight: Flight, customFields: FlightTooltipFieldItem[]): FlightTooltipDto {
    return {
        id: flight.id,
        acid: flight.acid,
        progressPercentage: flight.progressPercentage != null ? Math.round(flight.progressPercentage * 100) : null,
        arrivalTime: flight.arrivalTime,
        departureTime: flight.departureTime,
        destination: flight.destination,
        origin: flight.origin,
        hasNotes: flight.hasNotes,
        customFields: customFields
    };
}

export function flight2ContextMenuDto(flight: Flight): FlightContextMenuDto {
    const oetd = flight.oetd ?? flight.departureTime;
    return {
        id: flight.id,
        status: flight.status,
        acid: flight.acid,
        origin: flight.origin,
        destination: flight.destination,
        alternate1: flight.extra?.destinationAlternateAirport1 ?? null,
        alternate2: flight.extra?.destinationAlternateAirport2 ?? null,
        otherAlternates:
            flight.extra?.alternateDestinations?.filter((x) => x.type === AlternateDestinationRecordType.arrival).map((x) => x.id) ?? [],
        etopsAlternates:
            flight.extra?.alternateDestinations?.filter((x) => x.type === AlternateDestinationRecordType.etops).map((x) => x.id) ?? [],
        regId: flight.registrationId,
        oetd: oetd ? dayjs.unix(oetd).toISOString() : null,
        notam: flight.notam,
        runway: flight.runway
    };
}

export function flight2HoldingFlightDto(flight: Flight): HoldingFlightDto {
    return {
        id: flight.id,
        acid: flight.acid,
        altitude: flight.altitude
    };
}

export function flight2CurrentPositionDto(flight: Flight): FlightCurrentPositionDto {
    return {
        id: flight.id,
        origin: flight.origin,
        destination: flight.destination,
        originAirport: flight.originAirport,
        destinationAirport: flight.destinationAirport,
        onGround: flight.onGround,
        position: flight.position,
        status: flight.status,
        arrivalTime: flight.arrivalTime == null ? null : dayjs.unix(flight.arrivalTime).toISOString(),
        distanceToDestination: flight.distanceToDestination,
        heading: flight.heading
    };
}

export function impacts2Dto(impacts: ImpactRecord[]): FlightImpactDto[] {
    return impacts.map(
        (impact) =>
            ({
                id: impact.id,
                routeId: impact.routeId ?? -1,
                name: getImpactEventDescription(impact.eventCode),
                startTime: impact.enterTime,
                endTime: impact.exitTime,
                vendor: getImpactVendorName(impact.productVendor),
                severity: impact.severity,
                altitudeBase: impact.altitudeBase,
                altitudeTop: impact.altitudeTop,
                productCode: impact.productCode,
                productValidTime: impact.productValidTime,
                productModelTime: impact.productModelTime,
                monitorStatus: impact.monitorStatus,
                distanceOff: impact.distanceOff,
                startPointIdx: impact.startPointIdx,
                startPointOff: impact.startPointOff,
                resetPointIdx: impact.resetPointIdx,
                resetPointOff: impact.resetPointOff,
                startPosition: [impact.startPosition.lng, impact.startPosition.lat],
                endPosition: [impact.endPosition.lng, impact.endPosition.lat],
                eventCode: impact.eventCode
            }) satisfies FlightImpactDto
    );
}

export function flight2Dto(flight: Flight | null | undefined): FlightDetailsModel | null {
    if (!flight) {
        return null;
    }

    const breachedThresholdsCount = flight.thresholds?.thresholdsInfo.reduce((p, n) => (n.breachedLevel ? p + 1 : p), 0) ?? 0;

    return {
        acType: flight.aircraftType,
        acid: flight.acid,
        altitude: flight.altitude,
        arrivalTime: flight.arrivalTime,
        lastPositionReportTime: flight.lastPositionReportTime,
        status: flight.status,
        ground: flight.onGround,
        class: flight.class,
        departureTime: flight.departureTime,
        destination: flight.destination,
        destinationAirportPosition:
            flight.destinationAirport?.lat != null && flight.destinationAirport.lng != null
                ? Coordinate.fromLatLng(flight.destinationAirport.lat, flight.destinationAirport.lng)
                : Coordinate.empty,
        originAirportPosition:
            flight.originAirport?.lat != null && flight.originAirport.lng != null
                ? Coordinate.fromLatLng(flight.originAirport.lat, flight.originAirport.lng)
                : Coordinate.empty,
        destinationAlternate1: flight.extra?.destinationAlternateAirport1 ?? null,
        destinationAlternate2: flight.extra?.destinationAlternateAirport2 ?? null,
        distanceToDestination: flight.distanceToDestination,
        progressPercentage: flight.progressPercentage,
        feedId: flight.feedId,
        icaoId: flight.icao,
        in: flight.extra?.in ?? null,
        off: flight.extra?.off ?? null,
        on: flight.extra?.on ?? null,
        origin: flight.origin,
        out: flight.extra?.out ?? null,
        position: flight.position,
        lastReal: flight.lastRealPosition ?? Coordinate.empty,
        lastRealSpeed: flight.lastRealSpeed,
        lastRealAltitude: flight.lastRealAltitude,
        regId: flight.registrationId,
        scratchpad: flight.extra?.scratchpad ?? null,
        speed: flight.speed,
        userField1: flight.extra?.userField1 ?? null,
        id: flight.id,
        fuelOnBoard: flight.extra?.fuelOnBoard
            ? {
                  value: flight.extra.fuelOnBoard.value,
                  unit: flight.extra.fuelOnBoard.unit
              }
            : null,
        breachedThresholdsCount: breachedThresholdsCount,
        beacon: flight.beacon
    };
}

export function flight2FleetMonitor(flights: Flight[], airportManager: AirportManager): FleetMonitorFlightDto[] {
    return flights.map(
        (flight) =>
            ({
                acType: flight.aircraftType,
                acid: flight.acid,
                altitude: flight.altitude,
                arrivalTime: flight.arrivalTime,
                arrivalTimeDeviation: flight.arrivalDeviation,
                departureTime: flight.departureTime,
                departureTimeDeviation: flight.departureDeviation,
                destination: flight.destination,
                lastDestination: flight.oldDestination,
                destinationAlternate1: flight.extra?.destinationAlternateAirport1 ?? null,
                destinationAlternate2: flight.extra?.destinationAlternateAirport2 ?? null,
                progressPercentage: flight.progressPercentage,
                holding: flight.holding,
                in: flight.extra?.in != null,
                off: flight.extra?.off != null,
                on: flight.extra?.on != null,
                out: flight.extra?.out != null,
                onGround: flight.onGround,
                origin: flight.origin,
                regId: flight.registrationId,
                speed: flight.speed,
                status: flight.status,
                monitorStatus: flight.monitorStatus,
                edct: flight.extra?.edct ?? null,
                isPreDeparture:
                    flight.status >= FlightStatus.Planned ||
                    (flight.status === FlightStatus.Active && flight.onGround && (flight.distanceToDestination ?? 99) > 10),
                // Mon stats
                originMonitorStatus: airportManager.getMonitorStatusForAirport(flight.origin, 'origin'),
                destinationMonitorStatus: airportManager.getMonitorStatusForAirport(flight.destination, 'destination'),
                destinationAlternate1MonitorStatus: airportManager.getMonitorStatusForAirport(
                    flight.extra?.destinationAlternateAirport1,
                    'alternate'
                ),
                destinationAlternate2MonitorStatus: airportManager.getMonitorStatusForAirport(
                    flight.extra?.destinationAlternateAirport2,
                    'alternate'
                ),
                id: flight.id,
                fuelOnboard: flight.extra?.fuelOnBoard
                    ? {
                          value: flight.extra.fuelOnBoard.value,
                          unit: flight.extra.fuelOnBoard.unit
                      }
                    : null,
                taxiOutTime: flight.extra?.taxiType == 'D' ? flight.extra?.taxiTime : null,
                taxiInTime: flight.extra?.taxiType == 'A' ? flight.extra?.taxiTime : null
            }) satisfies FleetMonitorFlightDto
    );
}

export function flownRoute2Dto(route: { list: RouteFlown } | null): RouteFlownPointModel[] {
    return (
        route?.list?.map(
            (p) =>
                ({
                    position: p.position,
                    altitude: p.altitude,
                    time: p.time,
                    speed: p.speed,
                    fob: p.fob,
                    fobUnit: routeFlownUnitToFuelUnit(p.fobUnit),
                    feedId: p.feedId
                }) satisfies RouteFlownPointModel
        ) ?? []
    );
}

const impactDisplayedEventCodes: number[] = [
    // 1203 - 1206
    AlertEventCodes.aircraftImpactVolcAsh,
    AlertEventCodes.aircraftImpactSigmet,
    AlertEventCodes.aircraftImpactAIRMET,
    AlertEventCodes.aircraftImpactPIREPIcing,

    // 1209 - 1214
    AlertEventCodes.aircraftImpactTurbulence,
    AlertEventCodes.aircraftImpactIcing,
    AlertEventCodes.aircraftImpactConvSigmet,
    AlertEventCodes.aircraftImpactEDRTurb,
    AlertEventCodes.aircraftImpactPIREPTurb,
    AlertEventCodes.aircraftImpactDSUA,

    // 1216,1217
    AlertEventCodes.aircraftImpactIATATurb,
    AlertEventCodes.aircraftImpactGTGNTurb,

    // 1218, 1219
    AlertEventCodes.aircraftImpactContrail,
    AlertEventCodes.aircraftTcf
];

export function plannedRoute2Dto(flight: Flight, route: FeServerRouteModel | null): FlightPlanRouteModel | null {
    if (!route) {
        return null;
    }
    return {
        id: route.feRouteId,
        timestamp: route.ts ? dayjs(route.ts).unix() : null,
        text: route.rtxt,
        etops: etopsApi2DataModel(route.etops),
        cruisingAltitude: route.ca,
        fuelUnit: (route.fu ?? FuelUnit.Null) as unknown as FuelUnit,
        impacts: impacts2Dto(
            flight.impacts
                .values()
                .filter(
                    (x) =>
                        x.routeId === route.feRouteId &&
                        impactDisplayedEventCodes.includes(x.eventCode) &&
                        x.monitorStatus >= MonitorStatus.info
                )
                .toArray()
        ),
        waypoints:
            route.wp?.map(
                (x) =>
                    ({
                        position: [x.ln!, x.lt!],
                        name: x.id,
                        altitude: x.at ?? null, // Altitude can be in ft or 100ft depending on data provider
                        fob: x.fob ?? null,
                        type: (x.tp as WaypointType | undefined) ?? null
                    }) satisfies RouteWaypointModel
            ) ?? []
    };
}

export function routeAi2Dto(route: FeServerRouteAiResponse): RouteAnalytics {
    return {
        origin: route.analytics.iorg,
        destination: route.analytics.idest,
        time: dayjs(route.analytics.tm).unix(),
        segments: route.analytics.segments.map(
            (x) =>
                ({
                    sid: x.sid,
                    startPosition: [x.sln, x.slt],
                    endPosition: [x.eln, x.elt],
                    filedRoutesCount: x.pc,
                    atcRoutesCount: x.ac
                }) satisfies RouteAnalyticsSegment
        )
    };
}

export function etopsApi2DataModel(etops: FeServerEtopsModel | null): EtopsData | null {
    if (!etops) {
        return null;
    }

    return {
        borderDistance: etops.bd,
        borderTime: etops.bt,
        ruleDistance: etops.rd,
        ruleTime: etops.rt,
        airports:
            etops.apt
                ?.map((x) => {
                    const airport = x.icaoId ? sa.getByIcao(x.icaoId) : sa.getByIata(x.iataId);
                    if (!airport?.lng || !airport.lat) {
                        return null;
                    }
                    return {
                        position: [airport.lng, airport.lat] as Position,
                        iataId: x.iataId,
                        icaoId: x.icaoId,
                        aptFunc: x.aft
                    };
                })
                .filter((x) => x != null) ?? [],
        points:
            etops.pnt?.map((x) => ({
                name: x.pn,
                type: x.pt,
                position: [x.ln, x.lt],
                criticalTime: x.ct,
                fob: x.fob,
                fobUnit: x.fu,
                behindNdx: x.bai,
                aheadNdx: x.aai
            })) ?? []
    };
}
